import type { ComponentProps, Dispatch, ReactNode, SetStateAction } from 'react';
import {
  DatePicker,
  Input,
  Segmented,
  Select,
  Textarea,
  TimePicker,
  type PlainDate,
  type TimePickerValue,
} from '@viquard/doc-ui/components';
import { IcnAlert } from '@viquard/doc-ui/icons';
import type {
  CalendarEventLink,
  CalendarGroup,
} from '../../types.js';
import { useCalendarMessages } from '../../i18n.js';
import { AudienceSection } from './AudienceSection.js';
import { LocationSection } from './LocationSection.js';
import { RepeatSection } from './RepeatSection.js';
import { FieldRow, Section } from './layout.js';
import { buildDurationOptions } from './helpers.js';
import { buildLinkOptions } from './single-form-helpers.js';
import type {
  CalendarLocationType,
  CalendarMeetingProvider,
} from '../../utils/location.js';

type SelectOptions = ComponentProps<typeof Select>['options'];
type SegmentedOptions = ComponentProps<typeof Segmented>['options'];

interface SingleFormBodySectionsProps {
  readonly mobile: boolean;
  readonly locale?: string | undefined;
  readonly kind: string;
  readonly kindOptions: SegmentedOptions;
  readonly kindSelectOptions: SelectOptions;
  readonly onKindChange: (kind: string) => void;
  readonly title: string;
  readonly onTitleChange: Dispatch<SetStateAction<string>>;
  readonly titlePlaceholder: string;
  readonly error: string | null;
  readonly linkOptions: readonly CalendarEventLink[];
  readonly link: CalendarEventLink | null;
  readonly onLinkChange: Dispatch<SetStateAction<CalendarEventLink | null>>;
  readonly date: PlainDate | null;
  readonly onDateChange: Dispatch<SetStateAction<PlainDate | null>>;
  readonly time: TimePickerValue;
  readonly onTimeChange: Dispatch<SetStateAction<TimePickerValue>>;
  readonly allDay: boolean;
  readonly duration: string;
  readonly onDurationChange: Dispatch<SetStateAction<string>>;
  readonly remindersEnabled: boolean;
  readonly reminderOptions: SelectOptions;
  readonly reminderMinutesBefore: number | null;
  readonly onReminderMinutesBeforeChange: Dispatch<SetStateAction<number | null>>;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly groupId: string | null;
  readonly onGroupIdChange: Dispatch<SetStateAction<string | null>>;
  readonly crossPosted: readonly string[];
  readonly onCrossPostedChange: Dispatch<SetStateAction<readonly string[]>>;
  readonly locationType: CalendarLocationType | null;
  readonly onLocationTypeChange: Dispatch<SetStateAction<CalendarLocationType | null>>;
  readonly meetingProvider: CalendarMeetingProvider;
  readonly onMeetingProviderChange: Dispatch<SetStateAction<CalendarMeetingProvider>>;
  readonly meetingDetail: string;
  readonly onMeetingDetailChange: Dispatch<SetStateAction<string>>;
  readonly locationText: string;
  readonly onLocationTextChange: Dispatch<SetStateAction<string>>;
  readonly recurrenceEnabled: boolean;
  readonly recurring: boolean;
  readonly onRecurringChange: Dispatch<SetStateAction<boolean>>;
  readonly recurrence: string;
  readonly onRecurrenceChange: Dispatch<SetStateAction<string>>;
  readonly recurrenceUntil: PlainDate | null;
  readonly onRecurrenceUntilChange: Dispatch<SetStateAction<PlainDate | null>>;
  readonly meta: string;
  readonly onMetaChange: Dispatch<SetStateAction<string>>;
  readonly blockingMessage: string | null;
}

export function SingleFormBodySections(props: SingleFormBodySectionsProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    mobile,
    locale,
    kind,
    kindOptions,
    kindSelectOptions,
    onKindChange,
    title,
    onTitleChange,
    titlePlaceholder,
    error,
    linkOptions,
    link,
    onLinkChange,
    date,
    onDateChange,
    time,
    onTimeChange,
    allDay,
    duration,
    onDurationChange,
    remindersEnabled,
    reminderOptions,
    reminderMinutesBefore,
    onReminderMinutesBeforeChange,
    groups,
    groupId,
    onGroupIdChange,
    crossPosted,
    onCrossPostedChange,
    locationType,
    onLocationTypeChange,
    meetingProvider,
    onMeetingProviderChange,
    meetingDetail,
    onMeetingDetailChange,
    locationText,
    onLocationTextChange,
    recurrenceEnabled,
    recurring,
    onRecurringChange,
    recurrence,
    onRecurrenceChange,
    recurrenceUntil,
    onRecurrenceUntilChange,
    meta,
    onMetaChange,
    blockingMessage,
  } = props;

  return (
    <div className="vq-cal__composer-single-form">
      <Section title={messages.composerSectionKind}>
        {mobile ? (
          <Select options={kindSelectOptions} value={kind} onValueChange={onKindChange} size="md" />
        ) : (
          <Segmented
            options={kindOptions}
            value={kind}
            onChange={onKindChange}
            label={messages.composerEventKindLabel}
            size="md"
          />
        )}
      </Section>

      <Section title={messages.composerSectionWhat}>
        <div className={`vq-cal__composer-grid${linkOptions.length > 0 ? ' vq-cal__composer-grid--2' : ''}`}>
          <FieldRow label={messages.composerTitleLabel} filled={title.trim().length > 0} required>
            <Input
              value={title}
              onChange={(e) => { onTitleChange(e.target.value); }}
              placeholder={titlePlaceholder}
              autoFocus={!mobile}
              invalid={error !== null && title.trim() === ''}
              size="md"
            />
          </FieldRow>
          {linkOptions.length > 0 && (
            <FieldRow label={messages.composerLinkedLabel} filled={link !== null}>
              <Select
                options={buildLinkOptions(linkOptions, messages.composerLinkedNone)}
                value={link === null ? '__none__' : `${link.kind}:${link.id}`}
                onValueChange={(value) => {
                  if (value === '__none__') {
                    onLinkChange(null);
                    return;
                  }
                  const found = linkOptions.find((option) => `${option.kind}:${option.id}` === value);
                  onLinkChange(found ?? null);
                }}
                searchable
                size="md"
              />
            </FieldRow>
          )}
        </div>
      </Section>

      <Section title={messages.composerSectionWhen}>
        <div className="vq-cal__composer-grid vq-cal__composer-grid--3">
          <FieldRow label={messages.composerDateLabel} filled={date !== null} required>
            <DatePicker
              value={date}
              onChange={onDateChange}
              placeholder={messages.composerDatePlaceholder}
              clearLabel={messages.composerDatePickerClear}
              dialogLabel={messages.composerDatePickerDialogLabel}
              {...(locale !== undefined ? { locale } : {})}
              size="md"
              clearable={false}
            />
          </FieldRow>
          <FieldRow label={messages.composerTimeLabel} filled={!allDay}>
            <TimePicker value={time} onChange={onTimeChange} minuteStep={5} disabled={allDay} />
          </FieldRow>
          <FieldRow label={messages.composerDurationLabel} filled>
            <Select options={buildDurationOptions(messages)} value={duration} onValueChange={onDurationChange} size="md" />
          </FieldRow>
          {remindersEnabled && (
            <FieldRow label={messages.composerReminderLabel} filled={reminderMinutesBefore !== null}>
              <Select
                options={reminderOptions}
                value={reminderMinutesBefore === null ? 'none' : String(reminderMinutesBefore)}
                onValueChange={(value) => {
                  onReminderMinutesBeforeChange(value === 'none' ? null : Number(value));
                }}
                size="md"
              />
            </FieldRow>
          )}
        </div>
      </Section>

      <AudienceSection
        groups={groups}
        groupId={groupId}
        crossPosted={crossPosted}
        onGroupIdChange={onGroupIdChange}
        onCrossPostedChange={onCrossPostedChange}
      />

      <LocationSection
        locationType={locationType}
        meetingProvider={meetingProvider}
        meetingDetail={meetingDetail}
        locationText={locationText}
        onLocationTypeChange={onLocationTypeChange}
        onMeetingProviderChange={onMeetingProviderChange}
        onMeetingDetailChange={onMeetingDetailChange}
        onLocationTextChange={onLocationTextChange}
      />

      <RepeatSection
        enabled={recurrenceEnabled}
        recurring={recurring}
        recurrence={recurrence}
        recurrenceUntil={recurrenceUntil}
        onRecurringChange={onRecurringChange}
        onRecurrenceChange={onRecurrenceChange}
        onRecurrenceUntilChange={onRecurrenceUntilChange}
        locale={locale}
      />

      <Section title={messages.composerSectionNote}>
        <Textarea
          value={meta}
          onChange={(e) => { onMetaChange(e.target.value); }}
          rows={2}
          placeholder={messages.composerNotePlaceholder}
        />
      </Section>

      {blockingMessage !== null && (
        <p className="vq-cal__composer-error" role="alert">
          <IcnAlert size={12} />
          <span>{blockingMessage}</span>
        </p>
      )}
    </div>
  );
}
